import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Clock, FileX } from 'lucide-react';
import { prescriptionService } from '@/services/prescriptionService';
import PrescriptionCart from '@/components/PrescriptionCart';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useClinic } from '@/contexts/ClinicContext';

const PrescriptionLink = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['prescription-link', token],
    queryFn: () => prescriptionService.getPrescriptionLink(token!),
    enabled: !!token,
    retry: false, // Don't retry on error for prescription links
  });






  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Verifying Prescription
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            Please wait while we verify your prescription link...
          </p>
        </motion.div>
      </div>
    );
  }

  // Error States
  if (error || !data?.success) {
    const errorMessage = (error as any)?.message || 'Unknown error occurred';
    
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="border-red-200">
            <CardContent className="p-6 text-center">
              {errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('expired') ? (
                <>
                  <FileX className="h-16 w-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Prescription Link Not Found
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    This prescription link may have expired, been used already, or is invalid. 
                    Please contact your healthcare provider for a new link.
                  </p>
                </>
              ) : errorMessage.toLowerCase().includes('used') ? (
                <>
                  <Clock className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Prescription Already Processed
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    This prescription link has already been used to place an order. 
                    Each prescription link can only be used once.
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Unable to Load Prescription
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    {errorMessage}
                  </p>
                </>
              )}
              
              <Button onClick={() => navigate('/storefront')} className="w-full">
                Go to Storefront
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Check if prescription link is inactive or used
  const prescriptionLink = data.data.prescriptionLink;
  if (prescriptionLink.status !== 'active') {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="border-orange-200">
            <CardContent className="p-6 text-center">
              <Clock className="h-16 w-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                Prescription Link Inactive
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                This prescription link is no longer active. It may have expired or been used already.
                Please contact your healthcare provider for assistance.
              </p>
              <Button onClick={() => navigate('/storefront')} className="w-full">
                Go to Storefront
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Check if prescription has medications
  const prescription = data.data.prescription;
  const patient = data.data.patient;

  
  if ((!prescription?.medicationRequests) || prescription.medicationRequests.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card>
            <CardContent className="p-6 text-center">
              <FileX className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                No Medications Found
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                This prescription doesn't contain any medications to order.
              </p>
              <Button onClick={() => navigate('/storefront')} className="w-full">
                Go to Storefront
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Render the prescription cart
  return (
    <PrescriptionCart 
      patient={patient}
      onBack={() => navigate('/storefront')}
    />
  );
};

export default PrescriptionLink; 